/* eslint-disable */
import axios from 'axios';
import { showNotification } from '@mantine/notifications';
import { IconX } from '@tabler/icons';
import { createIntl, createIntlCache } from 'react-intl';
import {
  getImpersonatingEmail,
  getImpersonatingToken,
  getLanguage,
} from '../../../services/LocalStorageService';
import appLocales from '../../../lang';
import { HttpHeader } from '../../../types';

let impersonationEmail: string | null = getImpersonatingEmail();
let impersonationToken: string | null = getImpersonatingToken();

export const setImpersonationHeader = (email: string | null, token: string | null) => {
  impersonationEmail = email;
  impersonationToken = token;
};

export const axiosInstanceWithoutToken = axios.create({
  baseURL: `${process.env.REACT_APP_HOST_BACKEND}`,
  withCredentials: true, // IMPORTANT pour les cookies
  headers: {
    'Content-type': 'application/json',
  },
});

axiosInstanceWithoutToken.interceptors.request.use(config => {
  if (impersonationEmail) {
    config.headers[HttpHeader.X_SWITCH_USER] = impersonationEmail;
    config.headers[HttpHeader.X_SWITCH_TOKEN] = impersonationToken;
  }
  return config;
});

// Mutex local à l'onglet : sérialise les 401 simultanés du même contexte JS.
let isRefreshing = false;
let refreshSubscribers: ((success: boolean) => void)[] = [];

// Coordination inter-onglets :
//   1) navigator.locks sérialise le POST /api/auth/refresh entre les onglets,
//      pour éviter qu'un onglet B consomme un refresh_token déjà rotaté par A
//      (Cognito invalide alors les deux → tous les onglets se font /logout).
//   2) Le timestamp localStorage permet à l'onglet qui obtient le lock APRÈS
//      qu'un autre vient de refresh de ne pas re-refresh inutilement (les
//      cookies httpOnly sont déjà à jour côté browser).
//   3) BroadcastChannel propage le résultat du refresh aux autres onglets en
//      attente local (waitForRefresh) pour qu'ils débloquent leurs requêtes
//      sans avoir à re-tenter le refresh eux-mêmes.
const REFRESH_LOCK_NAME = 'mda-auth-refresh';
const REFRESH_TIMESTAMP_KEY = 'mda-auth-refreshed-at';
const REFRESH_FRESH_WINDOW_MS = 5_000;

const authChannel: BroadcastChannel | null =
  typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel('mda-auth') : null;

authChannel?.addEventListener('message', event => {
  if (event.data?.type === 'refresh-done') {
    isRefreshing = false;
    onRefreshComplete(event.data.success === true);
  }
});

function onRefreshComplete(success: boolean) {
  refreshSubscribers.forEach(callback => callback(success));
  refreshSubscribers = [];
}

function waitForRefresh(): Promise<boolean> {
  return new Promise(resolve => {
    refreshSubscribers.push(resolve);
  });
}

async function performRefreshInLock(): Promise<boolean> {
  // Si un autre onglet a refresh dans la dernière fenêtre, les cookies sont
  // déjà à jour → on saute le call réseau.
  const recentRefreshAt = parseInt(localStorage.getItem(REFRESH_TIMESTAMP_KEY) ?? '0', 10);
  if (Date.now() - recentRefreshAt < REFRESH_FRESH_WINDOW_MS) {
    return true;
  }

  try {
    const res = await axiosInstanceWithoutToken.get('/api/auth/refresh');
    const ok = res.status >= 200 && res.status < 300;
    if (ok) {
      localStorage.setItem(REFRESH_TIMESTAMP_KEY, Date.now().toString());
    }
    return ok;
  } catch {
    return false;
  }
}

async function performRefresh(): Promise<boolean> {
  if (typeof navigator !== 'undefined' && 'locks' in navigator) {
    // Lock cross-onglet — un seul onglet à la fois exécute le refresh réseau.
    return navigator.locks.request(REFRESH_LOCK_NAME, performRefreshInLock);
  }
  return performRefreshInLock();
}

export const getBaseApi = (headers = {}, requestConfigs = {}) => {
  const _axios = axios.create({
    baseURL: `${process.env.REACT_APP_HOST_BACKEND}`,
    withCredentials: true,
    headers: {
      'Content-type': 'application/json',
      'Accept-language': getLanguage(),
      ...headers,
    },
    ...requestConfigs,
  });
  _axios.interceptors.request.use(
    config => {
      if (impersonationEmail) {
        config.headers[HttpHeader.X_SWITCH_USER] = impersonationEmail;
        config.headers[HttpHeader.X_SWITCH_TOKEN] = impersonationToken;
      }
      return config;
    },
    error => {
      return Promise.reject(error);
    }
  );

  _axios.interceptors.response.use(undefined, error => {
    const localStorageSettings = localStorage.getItem('settings');
    const language = localStorageSettings
      ? JSON.parse(localStorageSettings).currentLanguage ?? 'fr'
      : 'fr';
    const { locale, messages } = appLocales[language];
    const cache = createIntlCache();
    const intl = createIntl({ locale, messages }, cache);
    if (error && error?.response?.status === 503) {
      showNotification({
        id: 'serviceUnavailable',
        message: intl.formatMessage({ id: 'serviceUnavailable' }),
        color: 'red',
        icon: <IconX size={16} />,
      });
    }
    return Promise.reject(error);
  });

  return _axios;
};

export const getApi = (headers = {}, requestConfigs = {}) => {
  const _axios = getBaseApi(headers, requestConfigs);

  _axios.interceptors.response.use(
    response => response,
    async error => {
      if (!error || error?.response?.status !== 401) {
        return Promise.reject(error);
      }

      const originalRequest = error.config;

      // Deuxième 401 après un refresh réussi = session vraiment morte → logout.
      if (originalRequest._retry) {
        window.location.href = '/logout';
        return Promise.reject(error);
      }
      originalRequest._retry = true;

      // Refresh déjà en cours dans cet onglet → attendre son issue.
      if (isRefreshing) {
        const success = await waitForRefresh();
        if (success) return _axios(originalRequest);
        window.location.href = '/logout';
        return Promise.reject(error);
      }

      isRefreshing = true;
      let success = false;
      try {
        success = await performRefresh();
      } finally {
        isRefreshing = false;
        onRefreshComplete(success);
        authChannel?.postMessage({ type: 'refresh-done', success });
      }

      if (success) return _axios(originalRequest);
      window.location.href = '/logout';
      return Promise.reject(error);
    }
  );

  return _axios;
};
